import { format } from 'date-fns';

import { BaseModel, type RelationConfig } from './base';

import { Customer } from './customer';
import { ReservationServiceModel } from './reservationServiceModel';

// Enums

export enum WaitlistStatus {
  waiting = 0,
  alerted = 1,
  notified = 2,
  offered = 3,
  booked = 4,
  expired = 5,
  cancelled = 6,
  available = 7,
}

export const WaitlistStatusLabels: Record<WaitlistStatus, string> = {
  [WaitlistStatus.waiting]: 'waitlist.waiting',
  [WaitlistStatus.alerted]: 'waitlist.alerted',
  [WaitlistStatus.notified]: 'waitlist.notified',
  [WaitlistStatus.offered]: 'waitlist.offered',
  [WaitlistStatus.booked]: 'waitlist.booked',
  [WaitlistStatus.expired]: 'general.expired',
  [WaitlistStatus.cancelled]: 'general.cancelled',
  [WaitlistStatus.available]: 'general.available',
};

// Main Model

export class Waitlist extends BaseModel {
  idWaitlist!: number;
  status!: WaitlistStatus;
  customer!: Customer | null;
  services!: ReservationServiceModel[];
  dates!: any[];
  availability!: { firstSlot?: any } | null;

  static override relations(): RelationConfig {
    return {
      customer: { type: Customer },
      services: { type: ReservationServiceModel },
    };
  }

  override objectID(): string | null {
    return this.idWaitlist?.toString() ?? null;
  }

  get datesLabel(): string[] {
    if (!this.dates?.length) return [];

    return this.dates.map((date: any) => {
      let dateLabel = '';
      let timeLabel = '';

      if (date.type === 'day') {
        dateLabel += date.day;
      }

      if (date.type === 'date' && date.date) {
        dateLabel += format(new Date(date.date * 1000), 'EEEE, d MMMM yyyy');
      }

      if (date.startTime || date.endTime) {
        const formatMinutes = (mins: number) => {
          const h = Math.floor(mins / 60);
          const m = mins % 60;
          const period = h >= 12 ? 'PM' : 'AM';
          const hour = h % 12 || 12;
          return m ? `${hour}:${m.toString().padStart(2, '0')} ${period}` : `${hour} ${period}`;
        };

        if (date.startTime && date.endTime) {
          timeLabel = `after ${formatMinutes(date.startTime)} and before ${formatMinutes(date.endTime)}`;
        } else if (date.startTime) {
          timeLabel = `after ${formatMinutes(date.startTime)}`;
        } else if (date.endTime) {
          timeLabel = `before ${formatMinutes(date.endTime)}`;
        }
      }

      return timeLabel ? `${dateLabel} ${timeLabel}`.trim() : dateLabel;
    });
  }

  get servicesLabel(): string {
    return this.services
      .map((service: any) => service.service?.title)
      .filter((value: string, index: number, array: string[]) => array.indexOf(value) === index)
      .join(', ');
  }

  get employeesLabel(): string {
    return this.services
      .map((service: any) => service.employee?.displayName)
      .filter((value: string, index: number, array: string[]) => array.indexOf(value) === index)
      .join(', ');
  }

  get statusColour(): string {
    switch (this.status) {
      case WaitlistStatus.waiting:
        return 'warning';
      case WaitlistStatus.booked:
      case WaitlistStatus.offered:
      case WaitlistStatus.notified:
        return 'success';
      case WaitlistStatus.alerted:
        return 'warning';
      case WaitlistStatus.expired:
      case WaitlistStatus.cancelled:
        return 'danger';
      default:
        return 'info';
    }
  }

  get hasAvailability(): boolean {
    return !!this.availability && !!this.availability.firstSlot;
  }

  get isCancelled(): boolean {
    return this.status === WaitlistStatus.cancelled;
  }

  get isBooked(): boolean {
    return this.status === WaitlistStatus.booked;
  }

  get isWaiting(): boolean {
    return this.status === WaitlistStatus.waiting;
  }
}
